import { getCookie, setCookie as setCookieUtil } from './cookieUtils'

export interface CookieConsentPreferences {
  analytics: boolean
  marketing: boolean
  timestamp: number
  version: string
}

const CONSENT_VERSION = '1.0.0'
const CONSENT_STORAGE_KEY = 'cookieConsent'
const COOKIE_NAME = 'cookieConsent'

declare global {
  interface Window {
    gtmInitialized: boolean
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
    gaInitialized: boolean
  }
}

/**
 * Get current cookie consent preferences from domain cookie (with localStorage fallback for migration)
 */
export const getCookieConsent = (): CookieConsentPreferences | null => {
  try {
    if (typeof window === 'undefined') return null

    // First, try to read from domain cookie (shared across subdomains)
    const cookieValue = getCookie(COOKIE_NAME)
    if (cookieValue) {
      try {
        const parsed = JSON.parse(cookieValue) as CookieConsentPreferences
        // Check if consent version matches (for future policy updates)
        if (parsed.version !== CONSENT_VERSION) return null
        return parsed
      } catch {
        // Invalid JSON in cookie, continue to localStorage fallback
      }
    }

    const stored = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as CookieConsentPreferences
        // Check if consent version matches
        if (parsed.version !== CONSENT_VERSION) return null
        // Migrate from localStorage to cookie (use the parsed object directly)
        setCookieUtil(COOKIE_NAME, JSON.stringify(parsed))
        return parsed
      } catch {
        // Invalid JSON in localStorage
      }
    }

    return null
  } catch {
    return null
  }
}

/**
 * Save cookie consent preferences to domain cookie (and localStorage for backward compatibility)
 */
export const setCookieConsent = (preferences: Omit<CookieConsentPreferences, 'timestamp' | 'version'> | CookieConsentPreferences): void => {
  if (typeof window === 'undefined') return

  // Handle both cases: preferences with or without timestamp/version
  const consent: CookieConsentPreferences =
    'timestamp' in preferences && 'version' in preferences
      ? (preferences as CookieConsentPreferences)
      : {
          ...preferences,
          timestamp: Date.now(),
          version: CONSENT_VERSION,
        }

  // Store in domain cookie (shared across shiptanbul.com and app.shiptanbul.com)
  setCookieUtil(COOKIE_NAME, JSON.stringify(consent))
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent))
  } catch {
    // localStorage might be disabled or full, continue without it
  }
}

/**
 * Check if user has given consent for analytics cookies
 */
export const hasAnalyticsConsent = (): boolean => {
  const consent = getCookieConsent()
  return consent?.analytics === true
}

/**
 * Check if user has given consent for marketing cookies
 */
export const hasMarketingConsent = (): boolean => {
  const consent = getCookieConsent()
  return consent?.marketing === true
}

/**
 * Check if user has given any consent
 */
export const hasAnyConsent = (): boolean => {
  const consent = getCookieConsent()
  return consent !== null
}

/**
 * Initialize Google Tag Manager (only if consent given)
 */
export const initGoogleTagManager = (): void => {
  if (!hasMarketingConsent()) return

  // Check if already initialized
  if (window.gtmInitialized) return

  // Show noscript iframe
  const noscript = document.getElementById('gtm-noscript')
  if (noscript) {
    noscript.style.display = 'block'
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(function (w: any, d: Document, s: string, l: string, i: string) {
    w[l] = w[l] || []
    w[l].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    })
    const f = d.getElementsByTagName(s)[0]
    const j = d.createElement(s) as HTMLScriptElement
    const dl = l != 'dataLayer' ? '&l=' + l : ''
    j.async = true
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
    f.parentNode?.insertBefore(j, f)
  })(window, document, 'script', 'dataLayer', 'GTM-5GDPC9H')

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(window as any).gtmInitialized = true
}

/**
 * Initialize Google Analytics (only if consent given)
 */
export const initGoogleAnalytics = (): void => {
  if (!hasAnalyticsConsent()) return

  // Check if already initialized
  if (window.gaInitialized) return

  // Load gtag script
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://www.googletagmanager.com/gtag/js?id=G-YNE040M5JR'
  document.head.appendChild(script)

  // Initialize gtag
  window.dataLayer = window.dataLayer || []
  function gtag(...args: unknown[]) {
    window.dataLayer.push(args)
  }
  window.gtag = gtag

  gtag('js', new Date())
  gtag('config', 'G-YNE040M5JR')
  gtag('config', 'AW-16566683152')
  gtag('event', 'Zeventpage', {
    zeventpage: 1,
  })
  window.gaInitialized = true
}

/**
 * Initialize all tracking scripts based on consent
 */
export const initTrackingScripts = (): void => {
  initGoogleTagManager()
  initGoogleAnalytics()
}
